import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import { SelectChangeEvent } from '@mui/material';

import getCalendars from '@/shared/api/bookings/calendars/getCalendars';
import syncCalendar from '@/shared/api/bookings/calendars/syncCalendar';
import viewContacts from '@/shared/api/bookings/contacts/viewContacts';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import BookingCalendar from '@/shared/types/bookings/calendar.type';
import BookingsContact from '@/shared/types/bookings/contact.type';

import BookingContactForm from '../../types/bookingContactForm.type';
import CustomCalendarsFormColumn from './components/CustomCalendarsFormColumn';

type AddContactFormProps = {
  contactsList?: BookingContactForm[];
  initialContact?: BookingContactForm;
  error?: string;
  formErrors?: {
    id: string;
    booking_contact_id: string;
    calendar_ids: string;
  };
  getFormValues: (value: BookingContactForm) => void;
  onRemove: () => void;
};

const AddContactForm: FC<AddContactFormProps> = ({
  contactsList,
  initialContact,
  error,
  formErrors,
  getFormValues,
  onRemove,
}) => {
  const { t } = useTranslation(['bookingsServices', 'forms', 'common']);
  const redirectLogin = useRedirectLogin();

  const [contact, setContact] = useState<BookingContactForm>({
    booking_contact_id: '',
    calendar_ids: [],
  });
  const [calendars, setCalendars] = useState<BookingCalendar[] | null>(null);
  const [contacts, setContacts] = useState<BookingsContact[] | null>(null);
  const [contactNameSearch] = useState('');
  const [title, setTitle] = useState('');
  const [isSyncButtonVisible, setIsSyncButtonVisible] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  useEffect(() => {
    if (initialContact) {
      setContact((prev) => ({
        ...prev,
        booking_contact_id: initialContact.booking_contact_id,
      }));
    }
  }, [initialContact]);

  useEffect(() => {
    const fetchContacts = async () => {
      const response = await viewContacts(
        {
          search: [
            {
              booking_contact_name: {
                cop: ['like'],
                value: contactNameSearch,
              },
            },
          ],
        },
        () => redirectLogin(),
      );

      if (response.success) {
        setContacts(response.data);
      } else {
        setContacts([]);
      }
    };

    void fetchContacts();
  }, [contactNameSearch, redirectLogin]);

  useEffect(() => {
    if (!contact.booking_contact_id) {
      setCalendars([]);
      return;
    }

    const fetchAssociatedCalendars = async () => {
      setIsSyncButtonVisible(false);

      const response = await getCalendars(contact.booking_contact_id, () =>
        redirectLogin(),
      );

      if (response.success) {
        setCalendars(response.data);

        if (initialContact) {
          setContact((prev) => ({
            ...prev,
            calendar_ids: initialContact.calendar_ids,
          }));
        }
      } else {
        setCalendars([]);
        setIsSyncButtonVisible(true);
      }
    };

    void fetchAssociatedCalendars();
  }, [contact.booking_contact_id, initialContact, redirectLogin]);

  const onBookingContactChange = (e: SelectChangeEvent) => {
    const { value } = e.target;

    setContact((prev) => ({
      ...prev,
      booking_contact_id: value,
      calendar_ids: [],
    }));

    setTitle(
      contacts?.find((contact) => contact.booking_contact_id === value)
        ?.booking_contact_name || '',
    );

    getFormValues({
      ...contact,
      booking_contact_id: value,
    });
  };

  const onSyncCalendar = async () => {
    const bookingServiceKey = contacts?.find(
      (contact) => contact.booking_contact_id === contact.booking_contact_id,
    )?.booking_service_key;

    setIsSyncing(true);
    setSyncError('');

    const response = await syncCalendar({
      body: { booking_service_key: bookingServiceKey || '' },
      id: contact.booking_contact_id,
      onUnauthorized: () => redirectLogin(),
    });

    if (response.success) {
      setCalendars(response.data);
      setIsSyncButtonVisible(false);
      setIsSyncing(false);
    } else {
      setIsSyncing(false);
      setSyncError(response.msg);
    }
  };

  return (
    <div>
      <WaveUIAccordion
        title={title || t('bookingsServices:addContact')}
        defaultExpanded
        square={false}
        elevation={0}
        actions={[
          {
            label: t('bookingsServices:removeContact'),
            icon: <DeleteIcon sx={{ width: 16, height: 16 }} />,
            onClick: onRemove,
          },
        ]}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <WaveUIForm_V2
          fields={[
            {
              field_type: FieldType.Select,
              field_props: {
                name: 'booking_contact_id',
                label: t('forms:booking_contact_id'),
                value: contact.booking_contact_id,
                options:
                  contacts?.map((contact) => {
                    return {
                      label: contact.booking_contact_name,
                      value: contact.booking_contact_id,
                      disabled: contactsList?.some(
                        (contactForm) =>
                          contactForm.booking_contact_id ===
                          contact.booking_contact_id,
                      ),
                    };
                  }) || [],
                placeholder: t('forms:booking_contact_id_placeholder'),
                onChange: onBookingContactChange,
                error: !!error || !!formErrors?.booking_contact_id,
                helperText: error || formErrors?.booking_contact_id,
              },
            },
            {
              columnRenderer(field) {
                return (
                  <CustomCalendarsFormColumn
                    field={field}
                    isSyncButtonVisible={isSyncButtonVisible}
                    isSyncing={isSyncing}
                    onSyncCalendar={onSyncCalendar}
                  />
                );
              },
              field_type: FieldType.MultiSelect,
              field_props: {
                name: 'calendar_ids',
                label: t('forms:calendar_ids'),
                value: contact.calendar_ids,
                options:
                  calendars?.map((calendar) => ({
                    label: calendar.calendar_name,
                    value: calendar.calendar_id,
                  })) || [],
                placeholder: t('forms:calendar_ids_placeholder'),
                multiple: true,
                error: !!syncError || !!formErrors?.calendar_ids,
                helperText: syncError || formErrors?.calendar_ids,
              },
            },
          ]}
          getFieldsValues={(value) => {
            setContact((prev) => ({ ...prev, ...value }));
            getFormValues({ ...contact, ...value });
          }}
        />
      </WaveUIAccordion>
    </div>
  );
};

export default AddContactForm;
